import { differenceInCalendarDays } from 'date-fns';
import { createDateSafe } from '@/utils/dateUtils';
import type {
    VacationDpCounts,
    VacationDpFilterKey,
    VacationDpRow,
} from '../models/vacationDpBoard.types';
import { hasNoHistoryLogged } from './vacationDataQuality';

/** Janela do cartão "A vencer em 30 dias". */
export const EXPIRING_WINDOW_DAYS = 30;

/** Ordem dos cartões na tela — derivada dos grupos. */
export const DP_FILTER_KEYS: readonly VacationDpFilterKey[] = [
    'awaitingApproval',
    'correctionPending',
    'sellPending',
    'expired',
    'expiring30',
    'noHistory',
    'conflict',
    'onVacation',
    'scheduled',
    'vacationsCompleted',
    'noRequest',
];

/**
 * Dias corridos até o prazo concessivo. Negativo significa vencido; `null`, sem prazo fechado
 * (colaborador ainda no primeiro período aquisitivo).
 */
export function daysUntilDeadline(row: VacationDpRow, today: Date = new Date()): number | null {
    const deadline = createDateSafe(row.concessiveDeadline);
    if (!deadline) return null;
    return differenceInCalendarDays(deadline, today);
}

/**
 * Vencido pelos dois caminhos possíveis: o saldo já acusa dias perdidos, ou o prazo passou sem que
 * o recálculo tenha rodado. O segundo caso importa mesmo com o job diário de reavaliação
 * (`syncVacationLifecycleScheduled`) no ar: ele roda 1×/dia, então entre o vencimento e a próxima
 * execução — ou se o job falhar um dia — o `expiredDays` gravado ainda não reflete a data. O
 * fallback por data cobre esse intervalo.
 */
export function isExpired(row: VacationDpRow, today: Date = new Date()): boolean {
    if (row.expiredDays > 0) return true;
    const legal = createDateSafe(row.legalDeadline);
    return legal !== null && differenceInCalendarDays(legal, today) < 0;
}

/** Limite para agendar em até 30 dias ou já passado, mas ainda sem vencimento CLT. Quem já venceu fica de fora. */
export function isExpiringSoon(row: VacationDpRow, today: Date = new Date()): boolean {
    if (isExpired(row, today)) return false;
    const days = daysUntilDeadline(row, today);
    return days !== null && days <= EXPIRING_WINDOW_DAYS;
}

/** Parada em alguma das três mesas de decisão. */
export function isAwaitingApproval(row: VacationDpRow): boolean {
    const status = row.request?.status;
    return status === 'PendingManager' || status === 'PendingClient' || status === 'PendingDP';
}

/** Aguardando correção de período pelo colaborador. */
export function isCorrectionPending(row: VacationDpRow): boolean {
    return row.request?.status === 'PendingCorrection';
}

/** Abono pecuniário pedido e ainda não decidido — decisão independente da de férias (art. 143). */
export function isSellPending(row: VacationDpRow): boolean {
    return row.request?.sellRequest.status === 'Pending';
}

/** Quem tem direito e não pediu nada. Inelegível não entra: não há o que cobrar dele. */
export function hasNoRequest(row: VacationDpRow): boolean {
    return row.eligible && row.request === null;
}

/** Elegível há 2+ anos sem nenhuma férias lançada no histórico. */
export function hasNoHistoryLaunched(row: VacationDpRow): boolean {
    return hasNoHistoryLogged({
        monthsWorked: row.monthsWorked,
        legacyTakenDays: row.legacyTakenDays,
        legacyScheduledDays: row.legacyScheduledDays,
        eligible: row.eligible,
    });
}

export function hasConflict(row: VacationDpRow): boolean {
    return (row.request?.conflictsSnapshot.length ?? 0) > 0;
}

export function hasCompletedVacations(row: VacationDpRow): boolean {
    return row.approvedPeriods.some((period) => period.status === 'Completed');
}

/**
 * De férias hoje — tem algum período aprovado com `startDate <= hoje <= endDate`.
 * A data sempre prevalece, independente do status da solicitação.
 *
 * O job diário `syncVacationLifecycleScheduled` já mantém `request.status` fiel a isso (inclusive
 * no fracionado — volta a `Scheduled` no intervalo entre parcelas). A derivação por data continua
 * aqui de propósito: o job roda 1×/dia, então esta função é quem cobre as horas entre o início real
 * e a próxima execução, e quem mantém o painel certo se o job falhar um dia.
 */
export function isOnVacation(row: VacationDpRow, today: Date = new Date()): boolean {
    return row.approvedPeriods.some((period) => {
        if (period.status === 'Completed') return false;
        const start = createDateSafe(period.startDate);
        const end = createDateSafe(period.endDate);
        if (!start || !end) return false;
        return (
            differenceInCalendarDays(today, start) >= 0 &&
            differenceInCalendarDays(end, today) >= 0
        );
    });
}

/**
 * Férias agendadas — tem algum período aprovado que ainda não começou (`startDate > hoje`).
 */
export function isScheduledAhead(row: VacationDpRow, today: Date = new Date()): boolean {
    return row.approvedPeriods.some((period) => {
        if (period.status === 'Completed') return false;
        const start = createDateSafe(period.startDate);
        if (!start) return false;
        return differenceInCalendarDays(start, today) > 0;
    });
}

export function matchesDpFilter(
    row: VacationDpRow,
    key: VacationDpFilterKey,
    today: Date = new Date()
): boolean {
    switch (key) {
        case 'expiring30':
            return isExpiringSoon(row, today);
        case 'expired':
            return isExpired(row, today);
        case 'awaitingApproval':
            return isAwaitingApproval(row);
        case 'correctionPending':
            return isCorrectionPending(row);
        case 'sellPending':
            return isSellPending(row);
        case 'noRequest':
            return hasNoRequest(row);
        case 'noHistory':
            return hasNoHistoryLaunched(row);
        case 'conflict':
            return hasConflict(row);
        case 'onVacation':
            return isOnVacation(row, today);
        case 'scheduled':
            return isScheduledAhead(row, today);
        case 'vacationsCompleted':
            return hasCompletedVacations(row);
    }
}

/**
 * Contagem de cada cartão.
 *
 * Sempre calculada sobre a lista **completa**, nunca sobre a filtrada: os números não podem mudar
 * quando o DP clica num cartão, senão o filtro parece ter apagado gente.
 */
export function countDpBuckets(
    rows: VacationDpRow[],
    today: Date = new Date()
): VacationDpCounts {
    const counts = {
        expiring30: 0,
        expired: 0,
        awaitingApproval: 0,
        correctionPending: 0,
        sellPending: 0,
        noRequest: 0,
        noHistory: 0,
        conflict: 0,
        onVacation: 0,
        scheduled: 0,
        vacationsCompleted: 0,
    } satisfies VacationDpCounts;

    rows.forEach((row) => {
        DP_FILTER_KEYS.forEach((key) => {
            if (matchesDpFilter(row, key, today)) counts[key] += 1;
        });
    });

    return counts;
}

/** Aplica o filtro ativo. `null` significa "sem filtro" e devolve tudo. */
export function filterDpRows(
    rows: VacationDpRow[],
    activeFilter: VacationDpFilterKey | null,
    today: Date = new Date()
): VacationDpRow[] {
    if (!activeFilter) return rows;
    return rows.filter((row) => matchesDpFilter(row, activeFilter, today));
}
